import { useEffect, useMemo, useState } from "react";
import { useLang } from "../context/LanguageContext";

const cleanText = (text) => {
  if (typeof text !== "string") return "";
  return text
    .replace(/restaurantname[:]?/gi, "")
    .replace(/restaurant name[:]?/gi, "")
    .replace(/اسم المطعم[:]?/gi, "")
    .trim();
};

// Shared across every component: one request per (language, text) for the whole session.
const translationCache = {};
const inFlight = new Map();

const cacheFor = (lang) => {
  if (!translationCache[lang]) translationCache[lang] = new Map();
  return translationCache[lang];
};

const translateViaBackend = (text, lang) => {
  const key = `${lang}:${text}`;
  if (inFlight.has(key)) return inFlight.get(key);
  const apiBase = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const request = fetch(`${apiBase}/translation/translate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  })
    .then((res) => {
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const translated = cleanText(data?.[lang]) || text;
      cacheFor(lang).set(text, translated);
      return translated;
    })
    .catch(() => text) // not cached: retried on the next mount
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
};

const useDynamicTranslation = (dataList = []) => {
  const { lang } = useLang();
  const [, setVersion] = useState(0);

  // Stable key so a new array with the same texts does not re-run the effect.
  const pendingKey = useMemo(() => {
    if (lang === "en") return "";
    const cache = cacheFor(lang);
    const texts = new Set();
    for (const raw of dataList) {
      const cleaned = cleanText(raw);
      if (cleaned && !cache.has(cleaned)) texts.add(cleaned);
    }
    return JSON.stringify([...texts]);
  }, [dataList, lang]);

  useEffect(() => {
    if (!pendingKey) return;
    const texts = JSON.parse(pendingKey);
    if (texts.length === 0) return;

    let isMounted = true;
    Promise.all(texts.map((text) => translateViaBackend(text, lang))).then(() => {
      if (isMounted) setVersion((v) => v + 1);
    });
    return () => {
      isMounted = false;
    };
  }, [pendingKey, lang]);

  const getText = (data) => {
    if (!data) return "";
    // Localized DB values { en, de, ar }: use the stored translation for the current language.
    if (typeof data === "object") {
      const stored = cleanText(data[lang]);
      if (stored) return stored;
    }
    const text =
      typeof data === "object"
        ? data.en || Object.values(data)[0]
        : String(data);
    const cleaned = cleanText(text);
    return (lang !== "en" && cacheFor(lang).get(cleaned)) || cleaned;
  };

  return { getText };
};

export default useDynamicTranslation;
